import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system/legacy';
import { Platform } from 'react-native';

import { offlineBlobStore, isBlobStoreAvailable } from './offline-blob-store';

import { appendNativeFile } from '@/utils/native-form-file';

const QUEUE_STORAGE_KEY = '@offline_sync_queue';
const MAX_RETRIES = 5;
const BASE_DELAY_MS = 2000;
// Marker prefix used in `localPath` on web to point at a record in IndexedDB
// (the actual file Blob is stored there, keyed by sync item id).
const WEB_BLOB_REF_PREFIX = 'idb:';

export type SyncItemStatus = 'pending' | 'syncing' | 'failed';

export interface SyncItem {
	id: string;
	type: 'file_upload' | 'mutation';
	status: SyncItemStatus;
	retryCount: number;
	createdAt: string;
	lastAttemptAt?: string;
	/** For file uploads */
	fileInfo?: {
		/** Local file URI on mobile; IndexedDB reference or fetchable URL on web. */
		localPath: string;
		fileName: string;
		mimeType: string;
		fileSize: number;
		attachmentType: string;
		workOrderId?: string;
		associationId?: string;
	};
	/** For generic mutations */
	mutationInfo?: {
		endpoint: string;
		method: string;
		body: string;
	};
	/** Human-readable label for UI */
	label: string;
}

type SyncListener = (items: SyncItem[]) => void;

class OfflineSyncService {
	private queue: SyncItem[] = [];
	private listeners: SyncListener[] = [];
	private processing = false;
	private initialized = false;
	private initializing: Promise<void> | null = null;

	async initialize(): Promise<void> {
		if (this.initialized) return;
		if (this.initializing) return this.initializing;

		this.initializing = this.loadQueue();
		await this.initializing;
	}

	private async loadQueue(): Promise<void> {
		let shouldNotify = false;

		try {
			const stored = await AsyncStorage.getItem(QUEUE_STORAGE_KEY);
			if (stored) {
				this.queue = JSON.parse(stored);
				shouldNotify = true;

				// Reset any items stuck in 'syncing' back to 'pending'
				for (const item of this.queue) {
					if (item.status === 'syncing') {
						item.status = 'pending';
					}
				}

				// On web, drop legacy items whose `localPath` is a `blob:` URL —
				// those URLs only live as long as the original page, so after a
				// reload the file is gone and the upload can never succeed.
				// Without this purge the queue gets permanently stuck saying
				// "Waiting for connection."
				if (Platform.OS === 'web') {
					const before = this.queue.length;
					this.queue = this.queue.filter(item => {
						const path = item.fileInfo?.localPath;
						if (!path) return true;
						return !path.startsWith('blob:');
					});
					if (this.queue.length !== before) {
						console.warn(`OfflineSyncService: dropped ${before - this.queue.length} unrecoverable blob-URL upload(s) from queue`);
					}
				}

				await this.persist();
			}
		} catch (error) {
			console.warn('OfflineSyncService: Failed to load queue from storage', error);
		} finally {
			this.initialized = true;
			this.initializing = null;
			if (shouldNotify) {
				this.notify();
			}
		}
	}

	/** Subscribe to queue changes. Returns unsubscribe function. */
	subscribe(listener: SyncListener): () => void {
		this.listeners.push(listener);
		// Send current state immediately
		listener([...this.queue]);
		this.initialize().catch(err => console.error('OfflineSyncService: initialization failed', err));
		return () => {
			this.listeners = this.listeners.filter(l => l !== listener);
		};
	}

	private notify(): void {
		const snapshot = [...this.queue];
		for (const listener of this.listeners) {
			listener(snapshot);
		}
	}

	private async persist(): Promise<void> {
		try {
			await AsyncStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(this.queue));
		} catch (error) {
			console.warn('OfflineSyncService: Failed to persist queue', error);
		}
	}

	getQueue(): SyncItem[] {
		return [...this.queue];
	}

	getPendingCount(): number {
		return this.queue.filter(i => i.status === 'pending' || i.status === 'failed').length;
	}

	isProcessing(): boolean {
		return this.processing;
	}

	/** Queue a file upload for later sync. Caches the file locally. */
	async queueFileUpload(params: {
		fileUri?: string;
		fileName: string;
		mimeType: string;
		fileSize: number;
		attachmentType: string;
		workOrderId?: string;
		associationId?: string;
		/** Optional: pre-fetched Blob (web). Avoids re-fetching a blob URL that may already be revoked. */
		blob?: Blob;
	}): Promise<SyncItem> {
		await this.initialize();

		const id = `sync_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
		let localPath = params.fileUri ?? '';

		if (Platform.OS === 'web') {
			// Persist the file's bytes in IndexedDB so the upload survives page
			// reloads. The queue's localStorage entry only holds a reference key.
			if (!isBlobStoreAvailable()) {
				throw new Error('Offline upload requires IndexedDB support');
			}
			let blob = params.blob;
			if (!blob) {
				if (!params.fileUri) {
					throw new Error('queueFileUpload on web requires `blob` or a fetchable `fileUri`');
				}
				const response = await fetch(params.fileUri);
				blob = await response.blob();
			}
			await offlineBlobStore.put(id, blob);
			localPath = `${WEB_BLOB_REF_PREFIX}${id}`;
		} else if (FileSystem.cacheDirectory) {
			// On mobile, copy file to a persistent cache directory
			if (!params.fileUri) {
				throw new Error('queueFileUpload on mobile requires a fileUri');
			}
			const cacheDir = `${FileSystem.cacheDirectory}offline-uploads/`;
			const dirInfo = await FileSystem.getInfoAsync(cacheDir);
			if (!dirInfo.exists) {
				await FileSystem.makeDirectoryAsync(cacheDir, { intermediates: true });
			}
			const cachedPath = `${cacheDir}${Date.now()}_${params.fileName}`;
			await FileSystem.copyAsync({ from: params.fileUri, to: cachedPath });
			localPath = cachedPath;
		}

		const item: SyncItem = {
			id,
			type: 'file_upload',
			status: 'pending',
			retryCount: 0,
			createdAt: new Date().toISOString(),
			label: `Upload ${params.fileName}`,
			fileInfo: {
				localPath,
				fileName: params.fileName,
				mimeType: params.mimeType,
				fileSize: params.fileSize,
				attachmentType: params.attachmentType,
				workOrderId: params.workOrderId,
				associationId: params.associationId,
			},
		};

		this.queue.push(item);
		await this.persist();
		this.notify();
		return item;
	}

	/** Queue a generic API mutation for later sync. */
	async queueMutation(params: {
		endpoint: string;
		method: string;
		body: Record<string, unknown>;
		label: string;
	}): Promise<SyncItem> {
		await this.initialize();

		const item: SyncItem = {
			id: `sync_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
			type: 'mutation',
			status: 'pending',
			retryCount: 0,
			createdAt: new Date().toISOString(),
			label: params.label,
			mutationInfo: {
				endpoint: params.endpoint,
				method: params.method,
				body: JSON.stringify(params.body),
			},
		};

		this.queue.push(item);
		await this.persist();
		this.notify();
		return item;
	}

	/** Remove a queued item (e.g., user cancels). Cleans up cached files. */
	async removeItem(id: string): Promise<void> {
		const item = this.queue.find(i => i.id === id);
		const localPath = item?.fileInfo?.localPath;
		if (localPath) {
			if (Platform.OS === 'web' && localPath.startsWith(WEB_BLOB_REF_PREFIX)) {
				try {
					await offlineBlobStore.delete(localPath.slice(WEB_BLOB_REF_PREFIX.length));
				} catch {
					// Ignore cleanup errors
				}
			} else if (Platform.OS !== 'web') {
				try {
					const info = await FileSystem.getInfoAsync(localPath);
					if (info.exists) {
						await FileSystem.deleteAsync(localPath, { idempotent: true });
					}
				} catch {
					// Ignore cleanup errors
				}
			}
		}
		this.queue = this.queue.filter(i => i.id !== id);
		await this.persist();
		this.notify();
	}

	/**
	 * Process all pending items in the queue.
	 * Pass the upload and mutation executors so this service stays decoupled
	 * from the API service (avoids circular deps).
	 */
	async processQueue(executors: {
		uploadFile: (formData: FormData) => Promise<any>;
		createAttachment: (data: Record<string, unknown>) => Promise<any>;
		fetchApi: (path: string, options: RequestInit) => Promise<any>;
	}): Promise<{ succeeded: number; failed: number }> {
		if (this.processing) return { succeeded: 0, failed: 0 };
		this.processing = true;
		this.notify();

		let succeeded = 0;
		let failed = 0;

		// Process items in order
		const pending = this.queue.filter(i => i.status === 'pending' || i.status === 'failed');

		for (const item of pending) {
			if (item.retryCount >= MAX_RETRIES) {
				// Permanently failed — leave in queue for user to dismiss
				failed++;
				continue;
			}

			item.status = 'syncing';
			item.lastAttemptAt = new Date().toISOString();
			this.notify();

			try {
				if (item.type === 'file_upload' && item.fileInfo) {
					await this.processFileUpload(item, executors);
				} else if (item.type === 'mutation' && item.mutationInfo) {
					await this.processMutation(item, executors);
				}

				// Success — remove from queue and clean up cached file
				await this.removeItem(item.id);
				succeeded++;
			} catch (error) {
				console.warn(`OfflineSyncService: Failed to sync item ${item.id}:`, error);
				item.status = 'failed';
				item.retryCount++;
				failed++;

				// Exponential backoff delay before next item
				const delay = Math.min(BASE_DELAY_MS * Math.pow(2, item.retryCount - 1), 30000);
				await new Promise(resolve => setTimeout(resolve, delay));
			}

			await this.persist();
			this.notify();
		}

		this.processing = false;
		this.notify();
		return { succeeded, failed };
	}

	private async processFileUpload(
		item: SyncItem,
		executors: {
			uploadFile: (formData: FormData) => Promise<any>;
			createAttachment: (data: Record<string, unknown>) => Promise<any>;
		},
	): Promise<void> {
		const info = item.fileInfo!;

		const formData = new FormData();

		if (Platform.OS === 'web') {
			let blob: Blob | null = null;
			if (info.localPath.startsWith(WEB_BLOB_REF_PREFIX)) {
				const blobId = info.localPath.slice(WEB_BLOB_REF_PREFIX.length);
				blob = await offlineBlobStore.get(blobId);
				if (!blob) {
					throw new Error('Queued file is missing from local storage');
				}
			} else {
				// Legacy `blob:`/`data:` URL — only valid in the same page session.
				// (initialize() purges blob: URLs after a reload, so this branch
				// only runs when the URL was created in the current session.)
				const response = await fetch(info.localPath);
				blob = await response.blob();
			}
			formData.append('file', blob, info.fileName);
		} else {
			appendNativeFile(formData, 'file', { uri: info.localPath, name: info.fileName, type: info.mimeType });
		}

		formData.append('fileName', info.fileName);
		formData.append('attachmentType', info.attachmentType);
		if (info.workOrderId) formData.append('workOrderId', info.workOrderId);
		if (info.associationId) formData.append('associationId', info.associationId);

		const uploadResult = await executors.uploadFile(formData);

		if (!uploadResult?.success) {
			throw new Error('Upload did not return success');
		}

		// Create the attachment metadata record
		await executors.createAttachment({
			workOrderId: info.workOrderId,
			associationId: info.associationId,
			fileName: uploadResult.fileName,
			fileType: uploadResult.fileType,
			mimeType: uploadResult.mimeType,
			fileSize: uploadResult.fileSize,
			storageKey: uploadResult.storageKey,
			attachmentType: info.attachmentType,
		});
	}

	private async processMutation(
		item: SyncItem,
		executors: { fetchApi: (path: string, options: RequestInit) => Promise<any> },
	): Promise<void> {
		const info = item.mutationInfo!;
		await executors.fetchApi(info.endpoint, {
			method: info.method,
			headers: { 'Content-Type': 'application/json' },
			body: info.body,
		});
	}
}

export const offlineSyncService = new OfflineSyncService();
